<script lang="ts" setup>
import { Head } from '@vueuse/head';
import visitingJapanPeachanLogo from '@/assets/svg/visiting-japan-peachan.svg';
import visitingJapanTokyoLogo from '@/assets/images/visiting-japan-tokyo.jpg';
import visitingJapanOsakaLogo from '@/assets/images/visiting-japan-osaka.jpg';
import visitingJapanKyotoLogo from '@/assets/images/visiting-japan-kyoto.jpg';
import visitingJapanLocalLogo from '@/assets/images/visiting-japan-local.jpg';
import LZString from 'lz-string';

import type { EventSearchQuery, FeaturedEventQuery } from '@/types/search';

const { trackEvent } = useGtm();
const profileStore = useProfileStore();
const router = useRouter();

const japanFeaturedQuery: Partial<FeaturedEventQuery> = {
	countryCode: 'jp',
};

const tokyoQuery: Partial<EventSearchQuery> = {
	countryCode: 'jp',
	type: EVENT_TYPE.PHYSICAL,
	sort: EVENT_SORT.DATETIME_ASC,
	peasy: 0,
	...(import.meta.env.VITE_ENV === ENVIRONMENT.PRODUCTION
		? { tagIds: DISCOVERY_TAG_GROUP_ID.TOKYO }
		: { location: '35.6803997,139.7690174' }),
};

const osakaQuery: Partial<EventSearchQuery> = {
	countryCode: 'jp',
	type: EVENT_TYPE.PHYSICAL,
	sort: EVENT_SORT.DATETIME_ASC,
	peasy: 0,
	...(import.meta.env.VITE_ENV === ENVIRONMENT.PRODUCTION
		? { tagIds: DISCOVERY_TAG_GROUP_ID.OSAKA }
		: { location: '34.6937249,135.5022535' }),
};

const kyotoQuery: Partial<EventSearchQuery> = {
	countryCode: 'jp',
	type: EVENT_TYPE.PHYSICAL,
	sort: EVENT_SORT.DATETIME_ASC,
	peasy: 0,
	...(import.meta.env.VITE_ENV === ENVIRONMENT.PRODUCTION
		? { tagIds: DISCOVERY_TAG_GROUP_ID.KYOTO }
		: { location: '35.011564,135.7681489' }),
};

const localQuery: Partial<EventSearchQuery> = {
	countryCode: 'jp',
	type: EVENT_TYPE.PHYSICAL,
	sort: EVENT_SORT.DATETIME_ASC,
	peasy: 0,
	...(import.meta.env.VITE_ENV === ENVIRONMENT.PRODUCTION
		? { tagIds: DISCOVERY_TAG_GROUP_ID.LOCAL }
		: { location: '35.6803997,139.7690174' }),
};

const discoverySections = computed(() => {
	return [
		{
			key: 'featured',
			title: "Japan's best events",
			subtitle: 'Skip the tourist traps. Join the locals.',
			fetchMode: 'featured' as const,
			featuredQuery: japanFeaturedQuery,
			logoSrc: visitingJapanPeachanLogo,
			logoAlt: 'Peachan',
		},
		{
			key: 'tokyo',
			title: "Tokyo's best events",
			subtitle: '',
			fetchMode: 'search' as const,
			searchQuery: tokyoQuery,
			logoSrc: visitingJapanTokyoLogo,
			logoAlt: 'Tokyo',
		},
		{
			key: 'osaka',
			title: "Osaka's best events",
			subtitle: '',
			fetchMode: 'search' as const,
			searchQuery: osakaQuery,
			logoSrc: visitingJapanOsakaLogo,
			logoAlt: 'Osaka',
		},
		{
			key: 'kyoto',
			title: "Kyoto's best events",
			subtitle: '',
			fetchMode: 'search' as const,
			searchQuery: kyotoQuery,
			logoSrc: visitingJapanKyotoLogo,
			logoAlt: 'Kyoto',
		},
		{
			key: 'local',
			title: 'More local favorites',
			subtitle: '',
			fetchMode: 'search' as const,
			searchQuery: localQuery,
			logoSrc: visitingJapanLocalLogo,
			logoAlt: 'Local',
		},
	];
});

onMounted(() => {
	trackEvent({
		event: GTM_EVENT_NAME.VIEW_VISITING_JAPAN_SUCCESS,
		user_id: profileStore.profileId,
	});
});

function handleExploreMoreEvents() {
	const criteria = [
		{
			index: 0,
			arrive: '',
			depart: '',
			location: '',
			tags: '',
			coordinates: '',
			countryCode: 'jp',
			type: EVENT_TYPE.PHYSICAL,
			sort: EVENT_SORT.DATETIME_ASC,
			peasy: 0,
		},
	];

	const compressedCriteria = LZString.compressToEncodedURIComponent(JSON.stringify(criteria));
	router.push({
		name: ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
		query: {
			[DISCOVERY_JAPAN_QUERY_NAME.SEARCH]: compressedCriteria,
		},
	});
}
</script>

<template>
	<Head>
		<title>Visiting Japan - Discover Local Events &amp; Experiences | Peatix</title>
		<meta
			name="description"
			content="Visiting Japan helps you uncover authentic local experiences, from workshops to neighborhood events, connecting you with Japan's unique subculture."
		>
		<link
			rel="canonical"
			href="https://peatix.com/us/visiting-japan"
		>
		<link
			rel="alternate"
			href="https://peatix.com/us/visiting-japan"
			hreflang="en-US"
		>
		<link
			rel="alternate"
			href="https://peatix.com"
			hreflang="x-default"
		>
		<meta
			property="og:title"
			content="Visiting Japan - Discover Local Events | Peatix"
		>
		<meta
			property="og:description"
			content="Visiting Japan helps you uncover authentic local experiences, from workshops to neighborhood events, connecting you with Japan's unique subculture."
		>
		<meta
			property="og:url"
			content="https://peatix.com/us/visiting-japan"
		>
		<meta
			property="og:image"
			content="https://peatix.com/static/images/visit-japan-og.png"
		>
		<meta
			property="og:type"
			content="website"
		>
		<meta
			name="twitter:card"
			content="summary_large_image"
		>
		<meta
			name="twitter:title"
			content="Visiting Japan - Discover Local Events | Peatix"
		>
		<meta
			name="twitter:description"
			content="Visiting Japan helps you uncover authentic local experiences, from workshops to neighborhood events, connecting you with Japan's unique subculture."
		>
		<meta
			name="twitter:image"
			content="https://peatix.com/static/images/visit-japan-og.png"
		>
	</Head>
	<div class="visit-japan-page">
		<VisitingJapanHero class="hero-section">
			<JapanDiscoveryForm />
		</VisitingJapanHero>
		<div class="discovery-area">
			<div
				v-for="(section, index) in discoverySections"
				:key="section.key"
				class="discovery-section-wrapper"
			>
				<JapanEventsSection
					class="discovery-section"
					:title="section.title"
					:subtitle="section.subtitle"
					:fetch-mode="section.fetchMode"
					:featured-query="section.featuredQuery"
					:search-query="section.searchQuery"
					:logo-src="section.logoSrc"
					:logo-alt="section.logoAlt"
					:tracking-section="section.key"
					:show-dots-decoration="index === 0"
				/>
			</div>
		</div>
		<div class="visit-japan-cta">
			<button
				class="visit-japan-cta-button"
				type="button"
				@click="handleExploreMoreEvents"
			>
				Explore more events
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.visit-japan-page {
	display: flex;
	flex-direction: column;
}

.hero-section {
	width: auto;
	margin: spacings-get(4);

	@include media-query-max(mobile) {
		margin: 0;
	}
}

.discovery-area {
	position: relative;
	overflow: hidden;
}

.discovery-section-wrapper {
	position: relative;
}

.visit-japan-cta {
	display: flex;
	justify-content: center;
	padding: spacings-get(8) 0;
	margin: 0 auto;
}

.visit-japan-cta-button {
	display: flex;
	height: rem(48);
	padding: spacings-get(2) spacings-get(6);
	justify-content: center;
	align-items: center;
	gap: spacings-get(2);
	border-radius: rem(35);
	background: linear-gradient(90deg, #ee4148 0%, #e7316e 100%);
	box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
	border: none;
	color: colors-get(base, white);
	cursor: pointer;
	@include fonts-get(semibold, text-md);

	@include media-query-max(mobile) {
		width: 100%;
		max-width: rem(420);
	}
}
</style>
